"use client";

import { H2, H1, H3, P, UniversalLink } from "@template/components";
import * as S from "./styles.ts";
import NumberPickerTabs, {
  INumberPickerTabsProps,
} from "@template/blocks/NumberPickerSection/component/NumberPickerTabs";
import Tables from "@template/blocks/NumberPickerSection/component/Tables";
import {
  NumberPickerFooter,
  NumberPickerFooterLink,
} from "./NumberPickerFooter";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import { useEffect, useState } from "react";
import { getInclusiveRemaining } from "@/redux/slices/numbers/thunks/getInclusiveRemaining.ts";
import ArchStandaloneSectionComponent from "@template/blocks/ArchStandaloneSection/component";
import { useSearchParams } from "next/navigation";
import useCurrency from "@/lib/helpers/hooks/useCurrency.ts";
import { changeNumberOption } from "@/redux/slices/numbers/index.ts";

const headingComponents = { H1, H2, H3 } as const;

interface INumberPickerProps {
  needHeading?: boolean;
  headingTag?: keyof typeof headingComponents;
  heading?: string;
  description?: string;
  default: INumberPickerTabsProps;
  altStyling?: boolean;
  context?: "virtualLandline";
  numberType?: {
    standard?: boolean;
    memorable?: boolean;
    nonGeo?: boolean;
  };
  inPageNavigation?: boolean;
  numberLimit?: number;
  siteVersion?: boolean;
}

const numberPickerFooterLinks: NumberPickerFooterLink[] = [
  {
    title: "London",
    href: "/phone-number/020-london/",
  },
  {
    title: "Birmingham",
    href: "/phone-number/0121-birmingham/",
  },
  {
    title: "Leeds",
    href: "/phone-number/0113-leeds/",
  },
  {
    title: "Glasgow",
    href: "/phone-number/0141-glasgow/",
  },
  {
    title: "Edinburgh",
    href: "/phone-number/0131-edinburgh/",
  },
  {
    title: "Liverpool",
    href: "/phone-number/0151-liverpool/",
  },
  {
    title: "Manchester",
    href: "/phone-number/0161-manchester/",
  },
  {
    title: "International Numbers",
    href: "/phone-number/international/",
  },
];

const usNumberPickFooterLinks: NumberPickerFooterLink[] = [
  {
    href: "/phone-number/929-new-york",
    title: "New York",
  },
  {
    href: "/phone-number/213-los-angeles",
    title: "Los Angeles",
  },
  {
    href: "/phone-number/773-chicago",
    title: "Chicago",
  },
  {
    href: "/phone-number/832-houston",
    title: "Houston",
  },
  {
    href: "/phone-number/628-san-francisco",
    title: "San Francisco",
  },
  {
    href: "/phone-number/857-boston",
    title: "Boston",
  },
  {
    href: "/phone-number/737-austin",
    title: "Austin",
  },
  {
    href: "/phone-number/international",
    title: "International Numbers",
  },
];

const NumberPicker = (props: INumberPickerProps) => {
  const {
    countryCode: defaultCountryCode = "77",
    areaCode: defaultAreaCode = "020",
    type: defaultAreaType = "local",
  } = props.default || {};

  const {
    needHeading,
    headingTag,
    heading,
    description,
    altStyling,
    context,
    numberType,
    inPageNavigation,
    numberLimit,
    siteVersion,
  } = props;
  const dispatch = useTypedDispatch();
  const cartItems = useTypedSelector((state) => state.user.cart.items);
  const [showTable, setShowTable] = useState(false);
  const currency = useCurrency();
  const searchParams = useSearchParams();
  const Heading = headingComponents[headingTag] ?? H1;
  useEffect(() => {
    const limitValue =
      numberLimit?.toString() || searchParams?.get("limit") || "8";

    dispatch(
      changeNumberOption({
        key: "limit",
        value: limitValue,
      })
    );
  }, [dispatch, numberLimit, searchParams]);

  const checkSearchParams = (type: string, defaultValue: string) => {
    if (searchParams?.size > 0) {
      return searchParams.get(type);
    } else {
      return defaultValue;
    }
  };

  const [searchCountry, setSearchCountry] = useState<string | null>(
    checkSearchParams("countryCode", defaultCountryCode)
  );
  const [searchArea, setSearchArea] = useState<string | null>(
    checkSearchParams("areaCode", defaultAreaCode)
  );

  useEffect(() => {
    dispatch(getInclusiveRemaining());
  }, [cartItems.filter((x) => !x.loading).length]);

  useEffect(() => {
    if (!currency) return;

    const countryMap = {
      "$": { country: "233", area: "929" },
      "£": { country: "77", area: "020" },
    };

    const selection = countryMap[currency];

    if (selection && siteVersion) {
      setSearchCountry(selection.country);
      setSearchArea(selection.area);
    }
  }, [siteVersion, currency]);

  useEffect(() => {
    if (!currency) return;

    if (
      currency === "$" &&
      defaultCountryCode === "77" &&
      (defaultAreaCode === "020" || defaultAreaCode === "20")
    ) {
      setSearchCountry(searchParams.get("countryCode") ?? "233");
      setSearchArea(searchParams.get("areaCode") ?? "929");
    }
  }, [currency, defaultCountryCode, defaultAreaCode, siteVersion]);

  return (
    <S.NumberPickerSectionWrapper
      $altStyling={altStyling}
      $hasHeading={needHeading}
    >
      <S.NumberPickerSectionContainer id="number-search">
        {altStyling && (
          <ArchStandaloneSectionComponent
            color="white"
            style={{
              position: "absolute",
              top: needHeading ? 260 : 160,
              zIndex: -2,
            }}
          />
        )}
        {needHeading && (
          <S.NumberPickerHeader $altStyling={altStyling}>
            <Heading>{heading}</Heading>
            <P $textAlign="center">{description}</P>
          </S.NumberPickerHeader>
        )}
        <NumberPickerTabs
          countryCode={searchCountry || defaultCountryCode}
          areaCode={searchArea || defaultAreaCode}
          type={siteVersion ? "local" : defaultAreaType}
          defaultKey="buy"
          context={context}
          setShowTable={setShowTable}
          shownTable={numberType}
          needHeading={needHeading}
        />
        {context === "virtualLandline" ? (
          <></>
        ) : context === "individual" ? (
          showTable ? (
            <>
              <Tables
                table={numberType}
                context={context}
                inPageNavigation={inPageNavigation}
                numberLimit={numberLimit}
              />
            </>
          ) : (
            <></>
          )
        ) : (
          <>
            <Tables
              inPageNavigation={inPageNavigation}
              numberLimit={numberLimit}
            />
            <NumberPickerFooter
              links={
                currency === "$"
                  ? usNumberPickFooterLinks
                  : numberPickerFooterLinks
              }
            />
          </>
        )}
      </S.NumberPickerSectionContainer>
    </S.NumberPickerSectionWrapper>
  );
};

export default NumberPicker;
